// تاریخ شمسی فقط در لبه (ADR-014): ذخیره UTC (timestamptz) یا «تاریخ کسب‌وکاری» محلی شعبه (date، yyyy-MM-dd).
import { format as formatGregorianDate } from 'date-fns';
import { format as formatJalali, newDate } from 'date-fns-jalali';
import { faDigits } from './digits';

export const DEFAULT_TIME_ZONE = 'Asia/Tehran';

const BUSINESS_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface ToJalaliOptions {
  /** منطقه زمانی شعبه یا مستأجر برای لحظه‌های UTC (پیش‌فرض Asia/Tehran). */
  timeZone?: string;
  /** الگوی date-fns-jalali (پیش‌فرض yyyy/MM/dd). */
  pattern?: string;
  /** ارقام فارسی (پیش‌فرض true). */
  persianDigits?: boolean;
}

/** اجزای ساعت دیواری یک لحظه در منطقه زمانی داده‌شده. */
function wallClock(instant: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
}

/**
 * تبدیل به رشته شمسی. ورودی `yyyy-MM-dd` تاریخ کسب‌وکاری است و بدون تبدیل منطقه زمانی نمایش داده می‌شود؛
 * لحظه (Date یا ISO با زمان) ابتدا به ساعت دیواری منطقه زمانی شعبه برده می‌شود.
 */
export function toJalali(value: Date | string | null | undefined, options: ToJalaliOptions = {}): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const { timeZone = DEFAULT_TIME_ZONE, pattern = 'yyyy/MM/dd', persianDigits = true } = options;

  let local: Date;
  const businessDate = typeof value === 'string' ? BUSINESS_DATE.exec(value) : null;
  if (businessDate) {
    local = new Date(Number(businessDate[1]), Number(businessDate[2]) - 1, Number(businessDate[3]));
  } else {
    const instant = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(instant.getTime())) {
      return '';
    }
    local = wallClock(instant, timeZone);
  }

  const text = formatJalali(local, pattern);
  return persianDigits ? faDigits(text) : text;
}

/** تاریخ و ساعت شمسی (yyyy/MM/dd HH:mm). */
export function toJalaliDateTime(
  value: Date | string | null | undefined,
  options: Omit<ToJalaliOptions, 'pattern'> = {},
): string {
  return toJalali(value, { ...options, pattern: 'yyyy/MM/dd HH:mm' });
}

/** نمایش میلادی تاریخ کسب‌وکاری yyyy-MM-dd. فقط لبه نمایش است و ذخیره را عوض نمی‌کند (ADR-014). */
export function toGregorian(value: string | null | undefined, pattern = 'EEEE d MMMM yyyy'): string {
  const businessDate = typeof value === 'string' ? BUSINESS_DATE.exec(value) : null;
  if (!businessDate) {
    return '';
  }
  const local = new Date(Number(businessDate[1]), Number(businessDate[2]) - 1, Number(businessDate[3]));
  return formatGregorianDate(local, pattern);
}

/** تاریخ کسب‌وکاری (yyyy-MM-dd میلادی برای API) از سال، ماه و روز شمسی. */
export function jalaliToBusinessDate(year: number, month: number, day: number): string {
  const date = newDate(year, month - 1, day);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** تاریخ کسب‌وکاری (yyyy-MM-dd) از Date محلی انتخاب‌شده در DatePicker. */
export function toBusinessDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
